/**
 * Composable for physics-based 360° viewer interactions
 * Provides momentum-based dragging with realistic friction and velocity
 */
export default (
  totalImages: number = 36,
  initialIndex: number = 0,
  sensitivity: number = 5,
  isTouchDevice: MaybeRefOrGetter<boolean> = ref(false),
) => {
  // State
  const currentImageIndex = ref(initialIndex);
  const isDragging = ref(false);
  const hasDragged = ref(false);
  const velocity = ref(0);
  const startX = ref(0);
  const lastInteractionTime = ref(Date.now());
  const animationFrameId = ref<number | null>(null);
  const lastDragTimes = ref<Array<{ time: number; diff: number }>>([]);

  // Constants for physics behavior
  const VELOCITY_WINDOW = 80; // Only consider movements in last 80ms
  const FRICTION_BASE = 0.88; // Base friction factor
  const FRICTION_INCREASE = 0.015; // Additional friction as velocity decreases
  const MIN_VELOCITY_THRESHOLD = 0.03; // Stop animation below this velocity
  const ROTATION_THRESHOLD = 1; // Minimum rotation needed to change image
  const TOUCH_SENSITIVITY = 2; // Touch sensitivity multiplier
  const FRAME_RATE_NORMALIZATION = 16; // Frame rate normalization for time delta (60fps = 16.67ms)
  const VELOCITY_MULTIPLIER = 0.2; // Velocity calculation multiplier
  const MAX_VELOCITY = 8; // Maximum velocity cap
  const MIN_INITIAL_VELOCITY = 0.2; // Minimum velocity to start momentum animation

  /**
   * Normalize image index to stay within bounds (0 to totalImages-1)
   */
  const normalizeIndex = (index: number): number => {
    return ((index % totalImages) + totalImages) % totalImages;
  };

  /**
   * Set image index to a specific value
   */
  const setImageIndex = (index: number) => {
    currentImageIndex.value = normalizeIndex(index);
  };

  /**
   * Stop any running animations
   */
  const stopAnimation = () => {
    if (animationFrameId.value !== null) {
      cancelAnimationFrame(animationFrameId.value);
      animationFrameId.value = null;
    }
  };

  /**
   * Start momentum-based animation after drag ends
   */
  const startMomentumAnimation = () => {
    stopAnimation();

    const initialVelocity = velocity.value;
    let lastAnimTime = Date.now();
    let currentVelocity = initialVelocity;
    let totalRotation = 0;

    // Don't animate if velocity is too low
    if (Math.abs(initialVelocity) < MIN_INITIAL_VELOCITY) {
      return;
    }

    const animate = () => {
      const now = Date.now();
      const timeDelta = Math.min(now - lastAnimTime, 20);
      lastAnimTime = now;

      // Apply non-linear friction with increasing resistance
      const frictionFactor =
        FRICTION_BASE -
        FRICTION_INCREASE *
          (1 -
            Math.min(
              Math.abs(currentVelocity) / Math.abs(initialVelocity || 0.1),
              1,
            ));

      currentVelocity *= frictionFactor;

      // Ensure velocity never increases after release
      if (initialVelocity > 0) {
        currentVelocity = Math.min(currentVelocity, initialVelocity);
      } else {
        currentVelocity = Math.max(currentVelocity, initialVelocity);
      }

      totalRotation += currentVelocity * (timeDelta / FRAME_RATE_NORMALIZATION);

      // Change images based on accumulated rotation
      while (totalRotation >= ROTATION_THRESHOLD) {
        totalRotation -= ROTATION_THRESHOLD;
        if (currentVelocity > 0) {
          setImageIndex(currentImageIndex.value + 1);
        } else {
          setImageIndex(currentImageIndex.value - 1);
        }
      }

      while (totalRotation <= -ROTATION_THRESHOLD) {
        totalRotation += ROTATION_THRESHOLD;
        if (currentVelocity > 0) {
          setImageIndex(currentImageIndex.value + 1);
        } else {
          setImageIndex(currentImageIndex.value - 1);
        }
      }

      // Stop animation when velocity is too low
      if (Math.abs(currentVelocity) < MIN_VELOCITY_THRESHOLD) {
        stopAnimation();

        return;
      }

      animationFrameId.value = requestAnimationFrame(animate);
    };

    animationFrameId.value = requestAnimationFrame(animate);
  };

  /**
   * Calculate velocity from recent drag movements
   */
  const calculateVelocity = (): number => {
    const now = Date.now();
    const recentMoves = lastDragTimes.value.filter(
      (move) => now - move.time <= VELOCITY_WINDOW,
    );

    if (recentMoves.length === 0) return 0;

    // Apply weight based on recency (newer moves get higher weights)
    let weightedDiffSum = 0;
    let weightSum = 0;

    recentMoves.forEach((move) => {
      const weight = 1 - (now - move.time) / VELOCITY_WINDOW;
      weightedDiffSum += move.diff * weight;
      weightSum += weight;
    });

    const avgDiff = weightSum > 0 ? weightedDiffSum / weightSum : 0;

    return Math.max(
      Math.min(avgDiff * VELOCITY_MULTIPLIER, MAX_VELOCITY),
      -MAX_VELOCITY,
    );
  };

  /**
   * Start drag interaction
   */
  const startDrag = (clientX: number) => {
    stopAnimation();
    isDragging.value = true;
    hasDragged.value = false; // Reset drag state
    startX.value = clientX;
    velocity.value = 0;
    lastDragTimes.value = [];
    lastInteractionTime.value = Date.now();
  };

  /**
   * Handle drag movement
   */
  const onDrag = (clientX: number) => {
    if (!isDragging.value) return;

    const now = Date.now();
    lastInteractionTime.value = now;

    const diff = clientX - startX.value;
    const currentSensitivity = toValue(isTouchDevice)
      ? TOUCH_SENSITIVITY
      : sensitivity;

    if (Math.abs(diff) > currentSensitivity) {
      hasDragged.value = true; // Mark that meaningful dragging occurred

      // Record movement for velocity calculation
      lastDragTimes.value.push({ time: now, diff });

      // Remove old movements outside the velocity window
      while (
        lastDragTimes.value[0] &&
        now - lastDragTimes.value[0].time > VELOCITY_WINDOW
      ) {
        lastDragTimes.value.shift();
      }

      // Change image based on drag direction
      if (diff > 0) {
        setImageIndex(currentImageIndex.value + 1);
      } else {
        setImageIndex(currentImageIndex.value - 1);
      }

      startX.value = clientX;
    }
  };

  /**
   * End drag interaction and start momentum if applicable
   */
  const endDrag = () => {
    if (!isDragging.value) return;

    isDragging.value = false;
    velocity.value = calculateVelocity();
    lastDragTimes.value = [];

    // Start momentum animation if velocity is significant
    if (Math.abs(velocity.value) > 0.1) {
      startMomentumAnimation();
    }
  };

  /**
   * Reset to initial state
   */
  const reset = () => {
    stopAnimation();
    currentImageIndex.value = initialIndex;
    isDragging.value = false;
    hasDragged.value = false;
    velocity.value = 0;
    lastDragTimes.value = [];
  };

  // Computed properties
  const isAnimating = computed(() => animationFrameId.value !== null);
  const canInteract = computed(() => !isAnimating.value || isDragging.value);

  // Cleanup on unmount
  onUnmounted(() => {
    stopAnimation();
  });

  return {
    // State
    currentImageIndex,
    isDragging: computed(() => isDragging.value),
    hasDragged: computed(() => hasDragged.value),
    isAnimating,
    canInteract,
    velocity: computed(() => velocity.value),

    // Methods
    startDrag,
    onDrag,
    endDrag,
    setImageIndex,
    reset,
    stopAnimation,

    // Utilities
    normalizeIndex,
  };
};
